import { css } from "@emotion/css";
import { borderWidth } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
import type { PopoverBasicHelpProps } from "../../Popover/PopoverBasicHelp";
import { FormDescription } from "../Primitives/FormDescription";
import { InputError } from "../Primitives/InputError";
import { Legend } from "../Primitives/Legend";
import type { DescriptionContent } from "../utils/descriptionWithLinks";
export interface RadioGroupNewContextValue<T extends string | boolean = string | boolean> {
    value: T;
    onChange: (value: T) => void;
    name?: string;
    disabled?: boolean;
    error?: string;
    groupId: string;
}
export const RadioGroupContext = React.createContext<RadioGroupNewContextValue<string | boolean> | undefined>(undefined);
export interface RadioGroupNewProps<T extends string | boolean> {
    /**
     * The label for the radio group.
     */
    label: string;
    /**
     * Optional description paragraph to display under the label.
     * Can be either:
     * - A simple string (basic usage)
     * - A DescriptionContent array created with descriptionText`...` for rich content with links and code
     * @example
     * // Simple string
     * description="This is a basic description."
     *
     * // Rich content with links and code
     * description={descriptionText`Set ${descriptionCode("isVisible")} prop or see ${descriptionLink("docs", "/docs")}.`}
     */
    description?: string | DescriptionContent;
    /**
     * PopoverBasicHelp component to display additional help information
     * displays the Information icon next to the group label
     */
    popover?: React.ReactElement<PopoverBasicHelpProps>;
    /**
     * If true displays the (optional) text next to the group label
     */
    hasOptionalMarker?: boolean;
    /**
     * If true displays the (required) text next to the group label
     */
    hasRequiredMarker?: boolean;
    /**
     * Disables the radio group
     */
    disabled?: boolean;
    /**
     * The children of the radio group (one or more RadioButtonNew components).
     */
    children: React.ReactNode;
    /**
     * Error message to display.
     */
    error?: string;
    /**
     * The value of the form field.
     */
    value: T;
    /**
     * The action to perform when the form control field is changed.
     */
    onChange: (newValue: T) => void;
    /**
     * These props hide the label and description visually and should be used when the radio group
     * is placed inside an expandable form section. Screen readers will still
     * announce them, but they won’t be shown to users, since the content is
     * already displayed within the expandable section.
     *
     * Do not use these props if the radio group is outside an expandable form section.
     * Instead you should ensure the label and optional description is displayed.
     *
     * These props can be removed when expandable form sections are removed.
     */
    hideLabel?: boolean;
    hideDescription?: boolean;
}
/**
 * RadioGroup component that supports rich descriptions with inline links.
 *
 * @example
 * ```tsx
 * import { RadioGroupNew, RadioButtonNew, descriptionText, descriptionLink } from '@octopusdeploy/design-system-components';
 *
 *     <RadioGroupNew
 *       label="Label for radio group"
 *       description={descriptionText`Add a description with a ${descriptionLink("link", "/link")}.`}
 *       value={value}
 *       onChange={setValue}
 *     >
 *       <RadioButtonNew value="option1" label="Automatic" name="deployment" />
 *       <RadioButtonNew value="option2" label="Manual" name="deployment" />
 *     </RadioGroupNew>
 *   );
 * };
 * ```
 */
export function RadioGroupNew<T extends string | boolean>({ label, description, children, error, hideLabel = false, hideDescription = false, hasOptionalMarker = false, hasRequiredMarker = false, popover, disabled, onChange, value, }: RadioGroupNewProps<T>) {
    const [showExternalError, setShowExternalError] = React.useState(true);
    const [previousError, setPreviousError] = React.useState(error);
    if (error !== previousError) {
        setPreviousError(error);
        setShowExternalError(true);
    }
    const handleChange = React.useCallback((newValue: string | boolean) => {
        setShowExternalError(false);
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        onChange?.(newValue as T);
    }, [onChange]);
    // TODO: React18 has a useID hook we can use here instead.
    const groupId = `radio-group-${Math.random().toString(36).substring(2, 9)}`;
    const contextValue: RadioGroupNewContextValue<string | boolean> = React.useMemo(() => ({
        value,
        onChange: handleChange,
        disabled,
        groupId,
        error,
    }), [value, handleChange, disabled, groupId, error]);
    return (<RadioGroupContext.Provider value={contextValue}>
            <fieldset className={radioGroupStyles} role="radiogroup" disabled={disabled}>
                <Legend label={label} hasOptionalMarker={hasOptionalMarker} hasRequiredMarker={hasRequiredMarker} popover={popover} disabled={disabled} hideLabel={hideLabel}/>
                {description && <FormDescription description={description} isDisabled={disabled} isGroupLabel={true} hideDescription={hideDescription}/>}
                {children}
                {showExternalError && <InputError error={error} id={`${groupId}-error`}/>}
            </fieldset>
        </RadioGroupContext.Provider>);
}
const radioGroupStyles = css({
    display: "grid",
    border: borderWidth.none,
    margin: 0,
    padding: 0,
    maxWidth: "600px",
});
